import { useState, useEffect, useCallback, useRef } from "react";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useFilterOptions from "../../utils/hooks/reports/useFilter";
import LoadingSpinner from "../../components/retests/utils/LoadingSpinner";
import { getCommentsFeed, CommentsFeedItem } from "../../utils/api/endpoints/reportCommentsApi";
import parse from "html-react-parser";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import axios from "../../utils/api/axios";
import {
  FiMessageSquare,
  FiCalendar,
  FiExternalLink,
  FiBriefcase,
  FiRotateCcw,
  FiAlertCircle,
  FiLayers,
  FiFolder,
  FiClock,
  FiUser,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiHash,
  FiActivity,
  FiSearch,
  FiX
} from "react-icons/fi";

// Role display names
const getRoleDisplayName = (role: UserRole | number | undefined): string => {
  const roleMapping: Partial<Record<UserRole, string>> = {
    [UserRole.BUSINESS_MANAGER]: "Program Manager",
    [UserRole.DEVELOPER]: "Security Engineer",
    [UserRole.BUSINESS_ADMINISTRATOR]: "Organization Admin",
    [UserRole.ADMIN_MANAGER]: "Admin Manager",
    [UserRole.QA]: "QA",
    [UserRole.SUB_ADMIN]: "Sub Admin",
    [UserRole.BUSINESS]: "Business Owner",
    [UserRole.ADMIN]: "Admin",
    [UserRole.RESEARCHER]: "Researcher"
  };
  return role ? (roleMapping[role as UserRole] || "User") : "User";
};

// Role display colors
const getRoleColor = (role: UserRole | number | undefined): string => {
  const colorMap: Partial<Record<UserRole, string>> = {
    [UserRole.BUSINESS_MANAGER]: "bg-blue-50 text-blue-700",
    [UserRole.DEVELOPER]: "bg-purple-50 text-purple-700",
    [UserRole.BUSINESS_ADMINISTRATOR]: "bg-indigo-50 text-indigo-700",
    [UserRole.ADMIN_MANAGER]: "bg-red-50 text-red-700",
    [UserRole.QA]: "bg-amber-50 text-amber-700",
    [UserRole.SUB_ADMIN]: "bg-orange-50 text-orange-700",
    [UserRole.BUSINESS]: "bg-emerald-50 text-emerald-700",
    [UserRole.ADMIN]: "bg-slate-100 text-slate-700",
    [UserRole.RESEARCHER]: "bg-cyan-50 text-cyan-700"
  };
  return role ? (colorMap[role as UserRole] || "bg-slate-50 text-slate-600") : "bg-slate-50 text-slate-600";
};

const CommentsPage = () => {
  usePageTitle("Unified Comments Feed");

  const [comments, setComments] = useState<CommentsFeedItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const limit = 10;

  // Filter States
  const [selectedProgram, setSelectedProgram] = useState<string>("");
  const [selectedSeverity, setSelectedSeverity] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedRoleGroup, setSelectedRoleGroup] = useState<string>("");
  const [selectedFeedTypes, setSelectedFeedTypes] = useState<string[]>(["report", "retest"]);
  const [timeRangePreset, setTimeRangePreset] = useState<string>("lifetime");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [companiesList, setCompaniesList] = useState<{ company_id: number; company_name: string }[]>([]);
  const [expandedComments, setExpandedComments] = useState<Set<number | string>>(new Set());
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const contentRefs = useRef<Map<number | string, HTMLDivElement>>(new Map());
  const [overflowMap, setOverflowMap] = useState<Map<number | string, boolean>>(new Map());

  const { filterOptions, loading: filterLoading } = useFilterOptions();

  // Debounce search input to avoid hitting backend on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Load companies for admin view
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await axios.get("/v2/companies/admin/names");
        if (res.status === 200 && res.data?.companies) {
          setCompaniesList(res.data.companies);
        }
      } catch (err) {
        console.error("Failed to fetch companies:", err);
      }
    };
    fetchCompanies();
  }, []);

  // Measure comment heights to detect overflow and show "Read More" button
  useEffect(() => {
    if (comments.length === 0) return;
    
    // Give browser brief window to render and calculate scroll heights
    const timer = setTimeout(() => {
      const newOverflowMap = new Map<number | string, boolean>();
      comments.forEach((com) => {
        const el = contentRefs.current.get(com.comment_id);
        if (el) {
          if (el.scrollHeight > 110) {
            newOverflowMap.set(com.comment_id, true);
          }
        }
      });
      setOverflowMap(newOverflowMap);
    }, 50);

    return () => clearTimeout(timer);
  }, [comments]);

  const toggleExpandComment = (commentId: number | string, e: React.MouseEvent) => {
    e.stopPropagation(); // Stop navigation to report card click
    setExpandedComments((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const fetchCommentsFeed = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getCommentsFeed({
        program: selectedProgram,
        severity: selectedSeverity,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        company: selectedCompany || undefined,
        roleGroup: selectedRoleGroup || undefined,
        feedTypes: selectedFeedTypes.join(","),
        search: debouncedSearch || undefined,
        page,
        limit
      });
      if (data) {
        setComments(data.comments);
        setTotalCount(data.count);
      }
    } catch (error) {
      console.error("Error fetching comments feed:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedProgram, selectedCompany, selectedRoleGroup, selectedSeverity, startDate, endDate, selectedFeedTypes, debouncedSearch, page]);

  useEffect(() => {
    fetchCommentsFeed();
  }, [fetchCommentsFeed]);

  const handleTimeRangePresetChange = (preset: string) => {
    setTimeRangePreset(preset);
    const today = new Date();
    
    if (preset === "lifetime") {
      setStartDate("");
      setEndDate("");
    } else {
      const endStr = today.toISOString().split("T")[0];
      const start = new Date();
      
      if (preset === "today") {
        // Start is today
      } else if (preset === "7days") {
        start.setDate(today.getDate() - 7);
      } else if (preset === "30days") {
        start.setDate(today.getDate() - 30);
      } else if (preset === "90days") {
        start.setDate(today.getDate() - 90);
      }
      
      const startStr = start.toISOString().split("T")[0];
      setStartDate(startStr);
      setEndDate(endStr);
    }
    setPage(1);
  };

  const handleResetFilters = () => {
    setSelectedProgram("");
    setSelectedSeverity("");
    setStartDate("");
    setEndDate("");
    setSelectedCompany("");
    setSelectedRoleGroup("");
    setSelectedFeedTypes(["report", "retest"]);
    setTimeRangePreset("lifetime");
    setSearchTerm("");
    setPage(1);
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity.toLowerCase()) {
      case "critical":
        return {
          accent: "border-l-[3px] border-l-red-500",
          pill: "bg-red-50 text-red-700 border border-red-200 font-bold"
        };
      case "high":
        return {
          accent: "border-l-[3px] border-l-orange-500",
          pill: "bg-orange-50 text-orange-700 border border-orange-200 font-bold"
        };
      case "medium":
        return {
          accent: "border-l-[3px] border-l-amber-500",
          pill: "bg-amber-50 text-amber-700 border border-amber-200 font-bold"
        };
      case "low":
        return {
          accent: "border-l-[3px] border-l-emerald-500",
          pill: "bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold"
        };
      default:
        return {
          accent: "border-l-[3px] border-l-blue-500",
          pill: "bg-blue-50 text-blue-700 border border-blue-200 font-bold"
        };
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleCardClick = (feedItem: CommentsFeedItem) => {
    if (feedItem.feedType === "retest" && feedItem.retestId) {
      window.open(`/dashboard/retests/${feedItem.retestId}`, "_blank");
    } else {
      window.open(`/dashboard/reports/${feedItem.reportId}#comments-section`, "_blank");
    }
  };

  const hasActiveFilters = selectedProgram || selectedSeverity || startDate || endDate || selectedCompany || selectedRoleGroup || timeRangePreset !== "lifetime" || searchTerm;
  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="montserrat-font min-h-screen bg-slate-50/50">
      <style>
        {`
          .comment-content {
            overflow-wrap: break-word;
            word-wrap: break-word;
            word-break: break-word;
            overflow-x: hidden;
            max-width: 100%;
          }
          .comment-content pre,
          .comment-content code {
            max-width: 100%;
            overflow-x: auto;
            word-break: break-word;
            overflow-wrap: break-word;
            white-space: pre-wrap;
          }
          .comment-content pre {
            background-color: #0f172a;
            color: #f8fafc;
            padding: 0.75rem;
            border-radius: 0.375rem;
            margin: 0.5rem 0;
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
            font-size: 0.8rem;
            line-height: 1.5;
          }
          .comment-content code {
            background-color: #f1f5f9;
            color: #0f172a;
            padding: 0.1rem 0.3rem;
            border-radius: 0.2rem;
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
            font-size: 0.8rem;
          }
          .comment-content img {
            max-width: 320px !important;
            max-height: 200px !important;
            width: auto !important;
            height: auto !important;
            display: block;
            margin: 0.5rem 0;
            border-radius: 0.375rem;
            border: 1px solid #e2e8f0;
            object-fit: contain;
            cursor: zoom-in;
            transition: opacity 0.15s;
          }
          .comment-content img:hover {
            opacity: 0.85;
          }
          .comment-content p {
            margin-bottom: 0.35rem;
            line-height: 1.5;
          }
          .comment-content p:last-child {
            margin-bottom: 0;
          }
          .comment-content a {
            color: #2563eb;
            text-decoration: underline;
            font-weight: 600;
          }
          .comment-content a:hover {
            color: #1d4ed8;
          }
          .comment-content * {
            max-width: 100%;
            box-sizing: border-box;
          }
          .cf-select {
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            padding-right: 1.75rem !important;
          }
          .comment-body {
            position: relative;
            transition: max-height 0.3s ease;
          }
          .comment-body.collapsed-active {
            max-height: 110px;
            overflow: hidden;
          }
          .comment-body.collapsed-active::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 40px;
            background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1));
            pointer-events: none;
          }
          .img-lightbox {
            position: fixed;
            inset: 0;
            z-index: 9999;
            background: rgba(0,0,0,0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: zoom-out;
            backdrop-filter: blur(4px);
          }
          .img-lightbox img {
            max-width: 90vw !important;
            max-height: 90vh !important;
            width: auto !important;
            height: auto !important;
            border-radius: 0.5rem;
            box-shadow: 0 25px 50px rgba(0,0,0,0.5);
            object-fit: contain;
          }
          .animate-fadeIn {
            animation: fadeIn 0.2s ease-out forwards;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-3px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* ─── HEADER ─── */}
        <div className="mb-6 flex items-center justify-between gap-4 px-1">
          <div className="flex items-center gap-4">
            <div 
              className="flex h-11 w-11 items-center justify-center rounded-xl shadow-sm"
              style={{ backgroundColor: 'rgba(88, 204, 2, 0.12)', border: '1px solid rgba(88, 204, 2, 0.1)' }}
            >
              <FiMessageSquare className="h-5.5 w-5.5 text-[#58CC02]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#010D2C] tracking-tight leading-tight">Comment Feed</h1>
              <p className="text-slate-400 font-semibold text-[10px] tracking-widest mt-0.5">
                Chronological comment history across programs
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 shadow-sm transition-all hover:bg-slate-50 hover:text-red-500"
              >
                <FiRotateCcw className="h-3 w-3" />
                Reset
              </button>
            )}
            <span className="flex h-9 items-center rounded-lg bg-[#010D2C] px-4 text-[10px] font-bold tracking-widest text-[#58CC02] shadow-sm">
              {totalCount} events
            </span>
          </div>
        </div>

        {/* ─── REDESIGNED MODERN & COMPACT FILTER BAR WITH SEARCH ─── */}
        <div className="mb-6 rounded-xl border border-slate-200 bg-white shadow-sm p-3">
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center justify-between">
            {/* Search Input Box */}
            <div className="relative flex-1 min-w-[280px]">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search comments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-9 py-2 text-[12px] font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all shadow-inner"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <FiX className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Filter Dropdowns Grid/Flex */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Organization */}
              <div className="relative flex items-center">
                <FiLayers className="absolute left-2.5 text-slate-400 w-3.5 h-3.5 pointer-events-none" />
                <select
                  value={selectedCompany}
                  onChange={(e) => { setSelectedCompany(e.target.value); setPage(1); }}
                  className="cf-select bg-slate-50 hover:bg-slate-100/70 rounded-lg border border-slate-200 pl-8 pr-7 py-2 text-[11px] font-semibold text-slate-700 focus:outline-none focus:border-slate-300 cursor-pointer transition-all"
                >
                  <option value="">All Companies</option>
                  {companiesList.map((comp) => (
                    <option key={comp.company_id} value={comp.company_id}>
                      {comp.company_name}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-2.5 text-slate-400 pointer-events-none w-3 h-3" />
              </div>

              {/* Program */}
              <div className="relative flex items-center">
                <FiBriefcase className="absolute left-2.5 text-slate-400 w-3.5 h-3.5 pointer-events-none" />
                <select
                  value={selectedProgram}
                  onChange={(e) => { setSelectedProgram(e.target.value); setPage(1); }}
                  className="cf-select bg-slate-50 hover:bg-slate-100/70 rounded-lg border border-slate-200 pl-8 pr-7 py-2 text-[11px] font-semibold text-slate-700 focus:outline-none focus:border-slate-300 cursor-pointer transition-all"
                >
                  <option value="">All Programs</option>
                  {filterOptions?.programNames.map((prog) => (
                    <option key={prog.id} value={prog.id}>
                      {prog.name}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-2.5 text-slate-400 pointer-events-none w-3 h-3" />
              </div>

              {/* Severity */}
              <div className="relative flex items-center">
                <FiAlertCircle className="absolute left-2.5 text-slate-400 w-3.5 h-3.5 pointer-events-none" />
                <select
                  value={selectedSeverity}
                  onChange={(e) => { setSelectedSeverity(e.target.value); setPage(1); }}
                  className="cf-select bg-slate-50 hover:bg-slate-100/70 rounded-lg border border-slate-200 pl-8 pr-7 py-2 text-[11px] font-semibold text-slate-700 focus:outline-none focus:border-slate-300 cursor-pointer transition-all"
                >
                  <option value="">All Severities</option>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
                <FiChevronDown className="absolute right-2.5 text-slate-400 pointer-events-none w-3 h-3" />
              </div>

              {/* Roles */}
              <div className="relative flex items-center">
                <FiUser className="absolute left-2.5 text-slate-400 w-3.5 h-3.5 pointer-events-none" />
                <select
                  value={selectedRoleGroup}
                  onChange={(e) => { setSelectedRoleGroup(e.target.value); setPage(1); }}
                  className="cf-select bg-slate-50 hover:bg-slate-100/70 rounded-lg border border-slate-200 pl-8 pr-7 py-2 text-[11px] font-semibold text-slate-700 focus:outline-none focus:border-slate-300 cursor-pointer transition-all"
                >
                  <option value="">All Roles</option>
                  <option value="business">Business Roles</option>
                  <option value="researcher">Researchers</option>
                  <option value="admin">Admin &amp; QA Team</option>
                </select>
                <FiChevronDown className="absolute right-2.5 text-slate-400 pointer-events-none w-3 h-3" />
              </div>

              {/* Time Preset */}
              <div className="relative flex items-center">
                <FiClock className="absolute left-2.5 text-slate-400 w-3.5 h-3.5 pointer-events-none" />
                <select
                  value={timeRangePreset}
                  onChange={(e) => handleTimeRangePresetChange(e.target.value)}
                  className="cf-select bg-slate-50 hover:bg-slate-100/70 rounded-lg border border-slate-200 pl-8 pr-7 py-2 text-[11px] font-semibold text-slate-700 focus:outline-none focus:border-slate-300 cursor-pointer transition-all"
                >
                  <option value="lifetime">Lifetime</option>
                  <option value="today">Today</option>
                  <option value="7days">7 Days</option>
                  <option value="30days">30 Days</option>
                  <option value="90days">90 Days</option>
                  <option value="custom">Custom</option>
                </select>
                <FiChevronDown className="absolute right-2.5 text-slate-400 pointer-events-none w-3 h-3" />
              </div>

              {/* Feed Types (Multi-select Pills) */}
              <div className="flex items-center gap-1.5 border-l border-slate-200 pl-2 ml-1">
                <button
                  onClick={() => {
                    let updated = [...selectedFeedTypes];
                    if (updated.includes("report")) {
                      if (updated.length > 1) {
                        updated = updated.filter(t => t !== "report");
                      }
                    } else {
                      updated.push("report");
                    }
                    setSelectedFeedTypes(updated);
                    setPage(1);
                  }}
                  className={`px-2.5 py-[7px] rounded-lg border text-[11px] font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer ${
                    selectedFeedTypes.includes("report")
                      ? "bg-[#010D2C] text-[#58CC02] border-[#010D2C]"
                      : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  <FiMessageSquare className="w-3.5 h-3.5" />
                  Report Comments
                </button>
                <button
                  onClick={() => {
                    let updated = [...selectedFeedTypes];
                    if (updated.includes("retest")) {
                      if (updated.length > 1) {
                        updated = updated.filter(t => t !== "retest");
                      }
                    } else {
                      updated.push("retest");
                    }
                    setSelectedFeedTypes(updated);
                    setPage(1);
                  }}
                  className={`px-2.5 py-[7px] rounded-lg border text-[11px] font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer ${
                    selectedFeedTypes.includes("retest")
                      ? "bg-[#010D2C] text-[#58CC02] border-[#010D2C]"
                      : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  <FiActivity className="w-3.5 h-3.5" />
                  Retest Logs
                </button>
              </div>
            </div>
          </div>

          {/* Date Range Inputs - Only shown when custom time range is active */}
          {timeRangePreset === "custom" && (
            <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3 justify-end">
              <span className="text-[10px] font-bold text-slate-400 uppercase">Custom Range:</span>
              <div className="flex items-center gap-1.5 animate-fadeIn">
                <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200 focus-within:border-slate-300 transition-all">
                  <FiCalendar className="text-slate-400 w-3.5 h-3.5 shrink-0" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setTimeRangePreset("custom");
                      setPage(1);
                    }}
                    className="bg-transparent text-[11px] font-semibold text-slate-700 focus:outline-none border-none p-0 w-[100px] cursor-pointer"
                  />
                </div>
                <span className="text-[9px] font-bold text-slate-300 uppercase">to</span>
                <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200 focus-within:border-slate-300 transition-all">
                  <FiCalendar className="text-slate-400 w-3.5 h-3.5 shrink-0" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setTimeRangePreset("custom");
                      setPage(1);
                    }}
                    className="bg-transparent text-[11px] font-semibold text-slate-700 focus:outline-none border-none p-0 w-[100px] cursor-pointer"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ─── CONTENT ─── */}
        {isLoading || filterLoading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : comments.length === 0 ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-slate-50 border border-slate-100 text-slate-400">
              <FiMessageSquare className="h-7 w-7" />
            </div>
            <h3 className="text-sm font-bold text-[#010D2C]">No Events Found</h3>
            <p className="mt-1.5 text-[11px] font-medium text-slate-400 max-w-xs leading-relaxed">
              No activities match your current filter criteria. Try adjusting date ranges or filters.
            </p>
            <button
              onClick={handleResetFilters}
              className="mt-4 rounded-lg bg-[#010D2C] px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-[#58CC02] shadow-sm hover:opacity-95 transition-all"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((com) => {
              const severityStyle = getSeverityStyles(com.severityCategory);
              const commenterRole = (com as any).role;

              return (
                <div
                  key={com.comment_id}
                  className={`bg-white rounded-lg border border-slate-200/80 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group/card ${severityStyle.accent}`}
                >
                  {/* ── Compact Card Header ── */}
                  <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      {/* Avatar */}
                      {com.pfp ? (
                        <img
                          src={com.pfp}
                          alt={com.postedBy}
                          className="h-6 w-6 rounded-full border border-slate-200 object-cover"
                        />
                      ) : (
                        <div className="h-6 w-6 rounded-full bg-[#010D2C]/5 border border-slate-200 flex items-center justify-center text-[9px] font-extrabold text-[#010D2C]">
                          {com.postedBy.substring(0, 2).toUpperCase()}
                        </div>
                      )}

                      {/* Name + Role */}
                      <span className="text-[12px] font-bold text-slate-800">{com.postedBy}</span>
                      {com.feedType === "retest" ? (
                        <span className="inline-flex items-center px-1.5 py-[1px] rounded text-[9px] font-bold leading-none bg-indigo-50 text-indigo-700 border border-indigo-200">
                          Retest Comment
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-[1px] rounded text-[9px] font-bold leading-none bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Report Comment
                        </span>
                      )}
                      {commenterRole !== undefined && (
                        <span className={`inline-flex items-center px-1.5 py-[1px] rounded text-[9px] font-bold leading-none ${getRoleColor(commenterRole)}`}>
                          {getRoleDisplayName(commenterRole)}
                        </span>
                      )}

                      <span className="text-[10px] text-slate-400 font-medium">on</span>

                      {/* Report link */}
                      <span
                        onClick={() => handleCardClick(com)}
                        className="text-[12px] font-bold text-[#010D2C] hover:text-blue-600 hover:underline cursor-pointer transition-colors truncate max-w-[280px]"
                      >
                        {com.vulnerabilityName}
                      </span>
                      <FiExternalLink className="h-3 w-3 text-blue-500 opacity-0 group-hover/card:opacity-100 transition-opacity shrink-0" />

                      {/* Severity pill */}
                      <span className={`inline-flex items-center rounded-full px-2 py-[2px] text-[8px] font-extrabold tracking-wider leading-none ${severityStyle.pill}`}>
                        {com.severityCategory}
                      </span>
                    </div>

                    {/* Timestamp + Meta */}
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="hidden sm:flex items-center gap-3 text-[10px] text-slate-400 font-medium">
                        <span className="flex items-center gap-1">
                          <FiFolder className="w-3 h-3" />
                          {com.programName}
                        </span>
                        <span className="flex items-center gap-1">
                          <FiLayers className="w-3 h-3" />
                          {com.orgName}
                        </span>
                        {com.runTitle && (
                          <span className="flex items-center gap-1 bg-indigo-50 text-indigo-600 px-1.5 py-[1px] rounded font-semibold">
                            <FiActivity className="w-3 h-3" />
                            {com.runTitle}
                            {com.runType && (
                              <span className="text-[8px] uppercase tracking-wider opacity-70">· {com.runType}</span>
                            )}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-semibold text-slate-400 flex items-center gap-1">
                        <FiClock className="h-3 w-3" />
                        {formatDate(com.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* ── Card Content ── */}
                  <div
                    onClick={() => handleCardClick(com)}
                    className="px-4 py-3 cursor-pointer hover:bg-slate-50/30 transition-colors"
                  >
                    <div 
                      ref={(el) => {
                        if (el) {
                          contentRefs.current.set(com.comment_id, el);
                        } else {
                          contentRefs.current.delete(com.comment_id);
                        }
                      }}
                      className={`comment-body ${
                        overflowMap.get(com.comment_id) && !expandedComments.has(com.comment_id)
                          ? "collapsed-active"
                          : "max-h-none overflow-visible"
                      }`}
                    >
                      <div className="comment-content text-[13px] text-slate-700 leading-relaxed font-medium">
                        {parse(com.comment || "")}
                      </div>
                    </div>

                    {overflowMap.get(com.comment_id) && (
                      <button
                        onClick={(e) => toggleExpandComment(com.comment_id, e)}
                        className="mt-2 text-[11px] font-bold text-blue-600 hover:text-blue-800 transition-colors focus:outline-none flex items-center gap-0.5"
                      >
                        {expandedComments.has(com.comment_id) ? "Read Less" : "Read More"}
                      </button>
                    )}
                  </div>

                  {/* ── Mobile-only meta strip ── */}
                  <div className="sm:hidden px-4 py-2 border-t border-slate-100 flex items-center gap-3 text-[9px] text-slate-400 font-medium">
                    <span className="flex items-center gap-1">
                      <FiFolder className="w-3 h-3" />
                      {com.programName}
                    </span>
                    <span className="flex items-center gap-1">
                      <FiLayers className="w-3 h-3" />
                      {com.orgName}
                    </span>
                    {com.runTitle && (
                      <span className="flex items-center gap-1 bg-indigo-50 text-indigo-600 px-1.5 py-[1px] rounded font-semibold">
                        <FiActivity className="w-3 h-3" />
                        {com.runTitle}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* ─── PAGINATION ─── */}
            {totalCount > limit && (
              <div className="mt-6 flex items-center justify-between pt-4">
                <span className="text-[10px] text-slate-400 font-semibold tracking-wide">
                  {((page - 1) * limit) + 1}–{Math.min(page * limit, totalCount)} of {totalCount}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-35 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    <FiChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <span className="px-3 py-1.5 text-[11px] font-bold text-slate-700 bg-white border border-slate-200 rounded-md shadow-sm">
                    {page} / {totalPages}
                  </span>
                  <button
                    disabled={page * limit >= totalCount}
                    onClick={() => setPage((p) => p + 1)}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-35 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    <FiChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsPage;
